import { useCallback } from "react";
import { getSAL2080_R01List, getPdfBlob_SAL2080_R01, downloadExcel_SAL2080_R01 } from "./Api_SAL2080_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showSuccess, showWarning, showError } from "src/utils/toast/toastUtil";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";
import { downloadExcel } from "src/utils/blob/blobUtil";
import { useSAL2080_R01Store, defaultSAL2080_R01Filter, SAL2080_R01Filter } from "./Store_SAL2080_R01";
import { SAL2080_R01Dto } from "./Dto_SAL2080_R01";
import { fetchDataSetHandler, fetchFilterConfig } from "src/utils/grid/GridHandlerContent";


interface SAL2080_R01ContentProps {
    setMessage: (message: string) => void;
}

export const Content_SAL2080_R01 = (props: SAL2080_R01ContentProps) => {
    const {
		setMessage,
    } = props;

    const { 
		SAL2080_R01Filter, setSAL2080_R01Filter,
		SAL2080_R01PastFilter, setSAL2080_R01PastFilter,
		SAL2080_R01List, setSAL2080_R01List, 
		selectedSAL2080_R01List,
		setAllCount,
		setScrollTop,
	} = useSAL2080_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	useFetchActionbarAuth("SAL2080_R01");

	const getParams = (filter: Partial<SAL2080_R01Filter>) => {
		return {
			compCd: filter.compCd,
			customCd: filter.customCd,
			taxType: filter.taxType,
			taxBilling: filter.taxBilling,
			issuedDateRange: filter.issuedDateRange,
			remark: filter.remark,
			fixedFlag: filter.fixedFlag
		};
	}



    const fetchData = useCallback(async () => {
        setScrollTop(null);
        setLoadingSpinner(true);
        try {

			const responseData = (await getSAL2080_R01List(
				'',
				getParams(SAL2080_R01Filter),
				SAL2080_R01List.length ?? 0
			));

            const newList = [...responseData.data.data ] as SAL2080_R01Dto[];
            setSAL2080_R01List(newList);
            
            if (newList && newList.length > 0) {
                setSAL2080_R01PastFilter(SAL2080_R01Filter);
                setSAL2080_R01List(newList);
                ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${newList.length}건)`);
                setTimeout(() => setMessage(""), 5000);
            } else {
                showWarning('데이터가 없습니다.');
                setSAL2080_R01List([]);
            }
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
            setSAL2080_R01List([]);
        } finally {
            setLoadingSpinner(false);
        }
    }, [SAL2080_R01Filter, setSAL2080_R01List, setSAL2080_R01PastFilter, setMessage, setScrollTop]);

	const printPdfWithMode = useCallback(async (mode: 'AGG' | 'EXPAND') => {
		const taxBillNo = selectedSAL2080_R01List?.[0];
		if (!taxBillNo) {
			showWarning("출력할 세금계산서를 선택하세요.");
			setMessage("출력할 세금계산서를 선택하세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_SAL2080_R01(taxBillNo, mode);
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					const win = window.open(fileURL, "_blank");
					if (win) win.addEventListener("load", () => win.print());
					showWarning("QZ로 출력할 수 없어 브라우저 인쇄 창을 띄웁니다.");
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			console.error("Print error:", error);
			showError("PDF 출력 중 오류가 발생했습니다.");
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedSAL2080_R01List, setMessage, printPdfFromBlob, setLoadingSpinner]);

	const handlePrintClick = useCallback(() => {
		printPdfWithMode('AGG');
	}, [printPdfWithMode]);

	const handlePrintDetailClick = useCallback(() => {
		printPdfWithMode('EXPAND');
	}, [printPdfWithMode]);
    
    const handleExcelClick = useCallback(async () => {

        const response = await downloadExcel_SAL2080_R01(
            '',
            getParams(SAL2080_R01Filter),
            SAL2080_R01List.length ?? 0
        );

        const binaryString = atob(response.data.data);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([byteArray]);
        downloadExcel(blob, '세금계산서 조회-excel.xlsx');
    }, [SAL2080_R01Filter, SAL2080_R01PastFilter, SAL2080_R01List.length]);
    
    return {
        fetchData,
        handleExcelClick,
        handlePrintClick,
        handlePrintDetailClick,
    };

};
